import dayjs from 'dayjs'
import { convertImageUrl } from '@/utils/imageUrl'

export function formatBoxOfficeAmount(yen) {
  const n = Number(yen)
  if (Number.isNaN(n)) {
    return { value: '-', unit: '' }
  }
  if (n >= 100000000) {
    const value = (n / 100000000).toFixed(2).replace(/\.?0+$/, '')
    return { value, unit: '億円' }
  }
  if (n >= 10000) {
    const value = (n / 10000).toLocaleString(undefined, { maximumFractionDigits: 1 })
    return { value, unit: '万円' }
  }
  return { value: n.toLocaleString(), unit: '円' }
}

export function formatPercent(value) {
  if (value == null || value === '') return '-'
  if (typeof value === 'string' && value.includes('%')) return value
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  const pct = n > 0 && n <= 1 ? n * 100 : n
  return `${Math.round(pct * 10) / 10}%`
}

export function formatWanCount(value) {
  const n = Number(value)
  if (Number.isNaN(n)) return '-'
  if (n >= 100000) {
    return (n / 10000).toLocaleString(undefined, { maximumFractionDigits: 1 })
  }
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 })
}

export function pickMarketRankList(res) {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object') return []

  const data = res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res
  const candidates = [
    data.movieList,
    data.movieRankList,
    data.rankList,
    data.movies,
    data.list,
    data.rows,
    data.records,
    Array.isArray(data) ? data : null,
  ]

  return candidates.find((item) => Array.isArray(item)) ?? []
}

export function pickMarketRankSummary(res) {
  if (!res || typeof res !== 'object') return {}

  const data = res.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : res
  return {
    ...data,
    ...(data.summary && typeof data.summary === 'object' ? data.summary : {}),
    ...(data.nationBoxInfo && typeof data.nationBoxInfo === 'object' ? data.nationBoxInfo : {}),
    ...(data.marketSummary && typeof data.marketSummary === 'object' ? data.marketSummary : {}),
    ...(data.total && typeof data.total === 'object' ? data.total : {}),
    ...(data.nationSummary && typeof data.nationSummary === 'object' ? data.nationSummary : {}),
  }
}

export function pickDailyTrendList(res) {
  if (Array.isArray(res)) return res
  if (!res || typeof res !== 'object') return []

  const data = res.data && typeof res.data === 'object' ? res.data : res
  const candidates = [
    data.dailyList,
    data.trendList,
    data.dailyTrend,
    data.list,
    data.rows,
    Array.isArray(data) ? data : null,
  ]

  return candidates.find((item) => Array.isArray(item)) ?? []
}

function formatReleaseInfo(item, selectedDate) {
  const days = item.releaseDays ?? item.releaseDay ?? item.openDays ?? item.daysSinceRelease
  if (days != null && days !== '') {
    return `公開${days}日目`
  }

  const openDate = item.openDate ?? item.releaseDate
  if (openDate && selectedDate) {
    const open = dayjs(openDate)
    if (open.isValid()) {
      const base = selectedDate.startOf('day')
      const openStart = open.startOf('day')
      const diff = base.diff(openStart, 'day') + 1
      if (diff > 0) return `公開${diff}日目`
      // 公開前の先行上映（前夜祭など）：公開日まであと何日かを表示。後端の releaseInfo と同じ文言に揃える
      return `上映前${openStart.diff(base, 'day')}日`
    }
  }

  return item.releaseInfo ?? ''
}

export function formatGenres(genres) {
  if (genres == null || genres === '') return ''
  if (Array.isArray(genres)) {
    return genres
      .map((item) => {
        if (item == null || item === '') return ''
        if (typeof item === 'string') return item
        if (typeof item === 'object') {
          return item.name ?? item.genre ?? item.label ?? ''
        }
        return String(item)
      })
      .filter(Boolean)
      .join(' / ')
  }
  return String(genres)
}

export function mapNationBoxInfo(summary) {
  const list = Array.isArray(summary.movieList)
    ? summary.movieList
    : Array.isArray(summary.movieRankList)
      ? summary.movieRankList
      : Array.isArray(summary.rankList)
        ? summary.rankList
        : Array.isArray(summary.list)
          ? summary.list
          : []
  const aggBoxFromList = list.reduce((acc, item) => {
    const n = Number(item?.boxOffice ?? item?.maxBoxOffice ?? item?.dailyBoxOffice ?? item?.amount ?? 0)
    return acc + (Number.isNaN(n) ? 0 : n)
  }, 0)
  const aggTicketsFromList = list.reduce((acc, item) => {
    const n = Number(item?.soldSeats ?? item?.seatOccupiedNo ?? item?.tickets ?? item?.audience ?? 0)
    return acc + (Number.isNaN(n) ? 0 : n)
  }, 0)
  const aggShowCountFromList = list.reduce((acc, item) => {
    const n = Number(item?.showCount ?? item?.countTotal ?? item?.scheduleCount ?? 0)
    return acc + (Number.isNaN(n) ? 0 : n)
  }, 0)

  // 「総興行収入」优先取点估计 totalBoxOffice；totalMaxBoxOffice 是区间上沿，单独展示会高约一成
  const boxRaw = summary.totalBoxOffice
    ?? summary.boxOffice
    ?? summary.totalMaxBoxOffice
    ?? summary.maxBoxOffice
    ?? summary.dailyBoxOffice
    ?? summary.totalMaxboxOffice
    ?? summary.totalMaxBoxoffice
    ?? (aggBoxFromList > 0 ? aggBoxFromList : undefined)
    ?? 0
  const boxFmt = formatBoxOfficeAmount(boxRaw)
  const tickets = summary.totalSoldSeats
    ?? summary.tickets
    ?? summary.audience
    ?? summary.seatOccupiedNo
    ?? summary.totalTickets
    ?? summary.totalSoldSeat
    ?? (aggTicketsFromList > 0 ? aggTicketsFromList : undefined)
    ?? 0
  const showCount = summary.totalScheduleCount
    ?? summary.showCount
    ?? summary.countTotal
    ?? summary.scheduleTotal
    ?? summary.totalShowCount
    ?? (aggShowCountFromList > 0 ? aggShowCountFromList : undefined)
    ?? 0

  return {
    title: '総興行収入',
    boxOffice: boxFmt.value,
    unit: boxFmt.unit,
    tickets: formatWanCount(tickets),
    showCount: formatWanCount(showCount),
  }
}

export function mapMovieRankItem(item, index, selectedDate) {
  // 「興行収入（AI推測値）」列取点估计 boxOffice；maxBoxOffice 仅作旧接口兼容的兜底
  const boxRaw = item.boxOffice
    ?? item.maxBoxOffice
    ?? item.dailyBoxOffice
    ?? item.amount
    ?? 0
  const sumBoxRaw = item.sumBoxOffice
    ?? item.totalBoxOffice
    ?? item.cumulativeBoxOffice
    ?? item.cumulativeMaxBoxOffice
    ?? item.sumBox
  const boxFmt = formatBoxOfficeAmount(boxRaw)
  const sumFmt = sumBoxRaw != null ? formatBoxOfficeAmount(sumBoxRaw) : null
  // 作品名下方的累计票房，同样优先点估计
  const cumulativeRaw = item.cumulativeBoxOffice ?? item.cumulativeMaxBoxOffice
  const cumulativeMaxBoxFmt = cumulativeRaw != null
    ? formatBoxOfficeAmount(cumulativeRaw)
    : null
  const avgShowView = item.avgShowView
    ?? item.avgScreen
    ?? item.screenAvg
    ?? item.avgScreenView

  return {
    id: item.movieId ?? item.id ?? index,
    movieId: item.movieId ?? item.id,
    originalRank: item.rank ?? item.ranking ?? item.sortNo ?? index + 1,
    name: item.movieName ?? item.title ?? item.name ?? '-',
    releaseInfo: formatReleaseInfo(item, selectedDate),
    sumBoxDesc: sumFmt ? `累計${sumFmt.value}${sumFmt.unit}` : (item.sumBoxDesc ?? ''),
    cumulativeMaxBoxOfficeText: cumulativeMaxBoxFmt
      ? `${cumulativeMaxBoxFmt.value}${cumulativeMaxBoxFmt.unit}`
      : '',
    boxOffice: boxFmt.value,
    unit: boxFmt.unit,
    // movieList mapping:
    // 興行シェア -> boxOfficeRate
    // 上映回数 -> scheduleCount
    // 上映シェア -> scheduleCountRate
    // 上映あたり平均動員 -> avgShowView
    // 充足率 -> seatOccupancyRate
    boxRate: formatPercent(item.boxOfficeRate ?? item.boxRate ?? item.boxShare),
    showCount: item.scheduleCount ?? item.showCount ?? item.countTotal ?? 0,
    showCountRate: formatPercent(item.scheduleCountRate ?? item.showCountRate ?? item.scheduleRate ?? item.showShare),
    avgShowView: avgShowView == null || avgShowView === '' ? '-' : avgShowView,
    avgSeatView: formatPercent(item.seatOccupancyRate ?? item.avgSeatView ?? item.emptyRate ?? item.seatRate ?? item.occupancyRate),
    category: item.category ?? item.categoryName ?? '',
    genres: formatGenres(item.genres ?? item.genre),
    imgUrl: convertImageUrl(item.poster ?? item.posterUrl ?? item.imgUrl ?? item.image ?? ''),
    boxTrends: mapDailyTrends(item.dailyTrend ?? item.boxTrends ?? item.trendList ?? []),
  }
}

export function mapDailyTrends(list) {
  if (!Array.isArray(list) || !list.length) return []

  const rows = list
    .map((item) => {
      const rawDate = item?.date ?? item?.day
      const d = rawDate != null ? dayjs(rawDate) : null
      const v = Number(
        item?.boxOffice ?? item?.maxBoxOffice ?? item?.amount ?? item?.value ?? item?.predictValue,
      )
      if (!d || !d.isValid() || Number.isNaN(v)) return null
      return { t: d.valueOf(), v, d }
    })
    .filter(Boolean)

  if (!rows.length) return []

  rows.sort((a, b) => a.t - b.t)
  const maxV = Math.max(...rows.map((row) => row.v), 1)

  return rows.map((row) => {
    const fmt = formatBoxOfficeAmount(row.v)
    const isToday = row.d.startOf('day').isSame(dayjs().startOf('day'))
    return {
      label: isToday ? '今日' : row.d.format('M/D'),
      value: `${fmt.value}${fmt.unit}`,
      height: Math.max(6, Math.round((row.v / maxV) * 52)),
    }
  })
}

/**
 * 公開週トレンド（/dp/movieWeeklyTrend の dailyList、金曜〜木曜の7日固定）をグラフ用に。
 * mapDailyTrends と違い、値 0 の日（未来日・上映なし）は最小の高さにとどめて「データがある」ように見せない。
 *
 * 今日〜明後日は日別予測（DEV-14975）を積み上げる：
 *   下段 = 販売済み（boxOffice）、上段 = 予測の残り（predictedBoxOffice - boxOffice）、棒の高さ = 予測値。
 *   predictedBoxOffice はサーバー側で販売済みを下回らないよう補正済み。縮尺は予測値を含めた最大値で揃える。
 */
export function mapWeeklyTrends(list) {
  if (!Array.isArray(list) || !list.length) return []

  const rows = list
    .map((item) => {
      const d = item?.date != null ? dayjs(item.date) : null
      if (!d || !d.isValid()) return null
      const v = Number(item?.boxOffice ?? item?.maxBoxOffice) || 0
      const status = item?.predictStatus || null
      const pv = status === 'AVAILABLE' ? Math.max(Number(item?.predictedBoxOffice) || 0, v) : null
      return {
        d,
        v,
        pv,
        status,
        predictTime: item?.predictTime || '',
        dayOfWeek: item?.dayOfWeek || '',
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.d.valueOf() - b.d.valueOf())

  if (!rows.length) return []

  const maxV = Math.max(...rows.map((row) => row.pv ?? row.v), 1)
  const today = dayjs().startOf('day')
  const toHeight = (v) => (v > 0 ? Math.max(6, Math.round((v / maxV) * 52)) : 2)
  const fmtYen = (v) => {
    const f = formatBoxOfficeAmount(v)
    return `${f.value}${f.unit}`
  }

  return rows.map((row) => {
    const isToday = row.d.startOf('day').isSame(today)
    const hasForecast = row.pv != null
    const height = toHeight(hasForecast ? row.pv : row.v)
    // 販売済みの段：予測がある日は総高さのうち販売済みの割合ぶん（0 円なら段を出さない）
    const soldHeight = hasForecast ? (row.v > 0 ? Math.max(2, Math.round((row.v / maxV) * 52)) : 0) : height
    return {
      // key 用（label は「今日」になり得るので日付そのものを別に持つ）
      date: row.d.format('YYYY-MM-DD'),
      label: isToday ? '今日' : row.d.format('M/D'),
      dayOfWeek: row.dayOfWeek,
      isWeekend: row.dayOfWeek === '土' || row.dayOfWeek === '日',
      isToday,
      // 予測がある日は予測値（=棒の高さ）を出す
      value: fmtYen(hasForecast ? row.pv : row.v),
      // 0 の日は 2px の線だけ出す（mapDailyTrends の最小 6px だと値があるように見える）
      height,
      hasForecast,
      soldHeight: Math.min(soldHeight, height),
      forecastHeight: hasForecast ? Math.max(height - soldHeight - (soldHeight > 0 ? 2 : 0), 0) : 0,
      // AVAILABLE / UNAVAILABLE / REGION_FILTERED / null（今日〜明後日以外）
      forecastStatus: row.status,
      predictTime: row.predictTime,
      tip: hasForecast
        ? `販売済み ${fmtYen(row.v)} / 予測 ${fmtYen(row.pv)}`
          + (row.predictTime ? `・${dayjs(row.predictTime).format('M/D H:mm')} 時点` : '')
        : '',
      // ホバー時のツールチップ用（予測のある日のみ）。tip の文字列を項目ごとに分けたもの
      tipInfo: hasForecast
        ? {
          title: `${row.d.format('M/D')}${row.dayOfWeek ? `（${row.dayOfWeek}）` : ''}`,
          sold: fmtYen(row.v),
          // pv は販売済みを下回らないよう補正済みなので差分は 0 以上
          remain: fmtYen(row.pv - row.v),
          total: fmtYen(row.pv),
          time: row.predictTime ? `${dayjs(row.predictTime).format('M/D H:mm')} 時点` : '',
        }
        : null,
    }
  })
}

/**
 * 公開週トレンドの予測凡例用：予測のある日・地域絞り込み中かどうか・最新の予測時刻
 * @returns {null | { hasForecast: boolean, regionFiltered: boolean, latestTime: string }}
 */
export function summarizeWeeklyForecast(trends) {
  if (!Array.isArray(trends) || !trends.some((t) => t.forecastStatus)) return null
  const times = trends.filter((t) => t.hasForecast && t.predictTime).map((t) => t.predictTime).sort()
  return {
    hasForecast: trends.some((t) => t.hasForecast),
    regionFiltered: trends.some((t) => t.forecastStatus === 'REGION_FILTERED'),
    latestTime: times.length ? dayjs(times[times.length - 1]).format('M/D H:mm') : '',
  }
}

export function createEmptyNationBoxInfo() {
  return {
    title: '総興行収入',
    boxOffice: '-',
    unit: '',
    tickets: '-',
    showCount: '-',
  }
}
